import {
  Component,
  inject,
  signal,
  computed,
  effect,
  input,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { PasswordModule } from 'primeng/password';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { PaginatorModule } from 'primeng/paginator';
import {
  ClientService,
  ClientCallback,
  ClientCallbackDto,
  ClientCallbackLog,
} from '../../../services/client';
import { DropdownItem } from '../../../services/dropdown';
import { ToastService } from '../../../services/toast';
import { IndDatePipe } from '../../../pipes/ind-date';

type LogScope = 'callback' | 'client';

@Component({
  selector: 'app-client-callbacks',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    InputNumberModule,
    PasswordModule,
    SelectModule,
    ToggleSwitchModule,
    PaginatorModule,
    IndDatePipe,
  ],
  templateUrl: './client-callbacks.html',
  styleUrls: ['../clients-shared.css', './client-callbacks.css'],
})
export class ClientCallbacksTab implements OnInit {
  /** When set, the grid shows just this one client (detail Callbacks tab); otherwise all clients. */
  clientId = input<number | null>(null);

  private fb = inject(FormBuilder);
  private svc = inject(ClientService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  /** Single-client mode (detail tab) hides the Client column / picker. */
  readonly singleMode = computed(() => this.clientId() != null);

  // Known events today; the dialog select is editable so future events can be typed in.
  readonly eventOptions = ['payment.success'];
  readonly methodOptions = ['POST', 'PUT', 'PATCH', 'GET'];
  readonly logScopeOptions: { label: string; value: LogScope }[] = [
    { label: 'This callback only', value: 'callback' },
    { label: "All of this client's callbacks", value: 'client' },
  ];

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  rows = signal<ClientCallback[]>([]);

  // All-clients mode: client filter over the grid + client picker in the dialog.
  clientOptions = signal<DropdownItem[]>([]);
  filterClientId = signal<number | null>(null);

  // ---- delivery-log dialog state ----
  showLogs = signal(false);
  logsLoading = signal(false);
  /** The callback whose log is open; null => the whole client (tab-level "View Logs"). */
  logsFor = signal<ClientCallback | null>(null);
  logsScope = signal<LogScope>('callback');
  logsRows = signal<ClientCallbackLog[]>([]);
  logsPage = signal(1);
  logsPageSize = signal(10);
  logsTotal = signal(0);
  expandedLogs = signal<Set<number>>(new Set());

  form: FormGroup = this.fb.group({
    id: [0],
    clientId: [null as number | null, Validators.required],
    eventName: ['payment.success', Validators.required],
    // Absolute http/https URL — the backend rejects anything else with a 400.
    callbackUrl: ['', [Validators.required, Validators.pattern(/^https?:\/\/\S+$/i)]],
    httpMethod: ['POST'],
    authenticationType: [''],
    headerKey: [''],
    headerValue: [''],
    timeoutInSeconds: [15, [Validators.required, Validators.min(1), Validators.max(60)]],
    retryCount: [1, [Validators.required, Validators.min(1), Validators.max(5)]],
    isActive: [true],
  });

  constructor() {
    // load() reads clientId()/filterClientId(), so this effect reloads the grid whenever either
    // changes — e.g. navigating /clients/12 → /clients/34 swaps the input without recreating
    // the tab component, and the all-clients filter reloads without a manual load() call.
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      this.load();
    });
  }

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.singleMode()) this.loadClients();
  }

  private loadClients() {
    this.svc.ddClients().subscribe({
      next: (opts) => this.clientOptions.set(opts ?? []),
    });
  }

  load() {
    this.loading.set(true);
    const id = this.clientId() ?? this.filterClientId();
    const req = id != null ? this.svc.cbGetByClient(id) : this.svc.cbGetAll();
    req.subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not load callbacks');
          this.rows.set([]);
          return;
        }
        const data = res?.data ?? [];
        this.rows.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  onFilterClient(v: number | null) {
    this.filterClientId.set(v ?? null); // the constructor effect reloads the grid
  }

  // ===================== display helpers =====================
  trunc(v?: string | null, n = 48): string {
    const s = (v ?? '').toString();
    if (!s) return '—';
    return s.length > n ? s.slice(0, n) + '…' : s;
  }

  methodSeverity(m?: string | null): 'info' | 'warn' | 'secondary' {
    switch ((m || 'POST').toUpperCase()) {
      case 'GET':
        return 'secondary';
      case 'PUT':
      case 'PATCH':
        return 'warn';
      default:
        return 'info'; // POST
    }
  }

  // ===================== add / edit =====================
  openAdd() {
    this.editing.set(false);
    this.form.reset({
      id: 0,
      clientId: this.clientId() ?? this.filterClientId(),
      eventName: 'payment.success',
      callbackUrl: '',
      httpMethod: 'POST',
      authenticationType: '',
      headerKey: '',
      headerValue: '',
      timeoutInSeconds: 15,
      retryCount: 1,
      isActive: true,
    });
    this.form.get('clientId')?.enable();
    this.showModal.set(true);
  }

  openEdit(cb: ClientCallback) {
    this.editing.set(true);
    this.form.reset({
      id: cb.id,
      clientId: cb.clientId ?? this.clientId(),
      eventName: cb.eventName ?? '',
      callbackUrl: cb.callbackUrl ?? '',
      httpMethod: (cb.httpMethod || 'POST').toUpperCase(),
      authenticationType: cb.authenticationType ?? '',
      headerKey: cb.headerKey ?? '',
      // The admin read returns the current secret so re-saving keeps it (revealed only here).
      headerValue: cb.headerValue ?? '',
      timeoutInSeconds: cb.timeoutInSeconds ?? 15,
      retryCount: cb.retryCount ?? 1,
      isActive: cb.isActive ?? true,
    });
    // A callback stays with its client — pick a client only at creation.
    this.form.get('clientId')?.disable();
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      const bad = (c: string) => this.form.get(c)?.invalid;
      if (bad('clientId')) this.toast.warning('Please choose a client.');
      else if (bad('eventName')) this.toast.warning('Event name is required.');
      else if (this.form.get('callbackUrl')?.hasError('required'))
        this.toast.warning('Callback URL is required.');
      else if (bad('callbackUrl'))
        this.toast.warning('Callback URL must be an absolute http:// or https:// URL.');
      else if (bad('timeoutInSeconds')) this.toast.warning('Timeout must be 1–60 seconds.');
      else if (bad('retryCount')) this.toast.warning('Retry count must be 1–5.');
      else this.toast.warning('Please fix the highlighted fields.');
      return;
    }

    const v = this.form.getRawValue();
    const dto: ClientCallbackDto = {
      id: this.editing() ? Number(v.id) : 0,
      clientId: this.clientId() ?? Number(v.clientId),
      eventName: (v.eventName ?? '').toString().trim(),
      callbackUrl: (v.callbackUrl ?? '').toString().trim(),
      httpMethod: (v.httpMethod || 'POST').toString().trim().toUpperCase(),
      authenticationType: this.nz(v.authenticationType),
      headerKey: this.nz(v.headerKey),
      headerValue: this.nz(v.headerValue),
      timeoutInSeconds: v.timeoutInSeconds != null ? Number(v.timeoutInSeconds) : 15,
      retryCount: v.retryCount != null ? Number(v.retryCount) : 1,
      isActive: !!v.isActive,
    };

    this.saving.set(true);
    this.svc.cbSave(dto).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          if (res?.statusCode === 409)
            this.toast.error(
              res?.message || 'A callback for this event and URL already exists for this client.',
            );
          else this.toast.error(res?.message || 'Failed to save callback');
          return;
        }
        this.toast.success(
          res?.message || (this.editing() ? 'Callback updated' : 'Callback created'),
        );
        this.closeModal();
        this.load();
      },
      error: (err: any) => {
        this.saving.set(false);
        if (err?.status === 409)
          this.toast.error(
            err?.error?.message ||
              'A callback for this event and URL already exists for this client.',
          );
        else this.toast.error(err?.error?.message || 'Failed to save callback');
      },
    });
  }

  // ===================== row actions =====================
  toggleActive(cb: ClientCallback) {
    const next = !cb.isActive;
    this.patchRow(cb.id, { isActive: next });
    this.svc.cbSetActive(cb.id, next).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(cb.id, { isActive: cb.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchRow(cb.id, { isActive: cb.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  async remove(cb: ClientCallback) {
    const ok = await this.toast.confirm(
      `The "${cb.eventName}" callback to ${this.trunc(cb.callbackUrl, 60)} will no longer fire.`,
      'Delete callback?',
      'Delete',
    );
    if (!ok) return;
    this.svc.cbDelete(cb.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete callback');
          return;
        }
        this.toast.success(res?.message || 'Callback removed');
        this.load();
      },
    });
  }

  // ===================== delivery log =====================
  openLogs(cb?: ClientCallback) {
    this.logsFor.set(cb ?? null);
    this.logsScope.set(cb ? 'callback' : 'client');
    this.logsPage.set(1);
    this.logsRows.set([]);
    this.logsTotal.set(0);
    this.expandedLogs.set(new Set());
    this.showLogs.set(true);
    this.loadLogs();
  }

  loadLogs() {
    this.logsLoading.set(true);
    const cb = this.logsFor();
    const narrow = this.logsScope() === 'callback' && !!cb;
    // Widened scope still stays inside the callback's client; tab-level opens use the
    // grid's client (or every client when the all-clients grid is unfiltered).
    const clientId = cb?.clientId ?? this.clientId() ?? this.filterClientId() ?? undefined;
    this.svc
      .cbGetLogs(clientId, narrow ? cb!.id : undefined, this.logsPage(), this.logsPageSize())
      .subscribe({
        next: (res: any) => {
          this.logsLoading.set(false);
          if (res?.success === false) {
            this.toast.error(res?.message || 'Could not load the delivery log');
            return;
          }
          const d = res?.data ?? {};
          this.logsRows.set(d.items ?? []);
          this.logsTotal.set(d.total ?? 0);
        },
        error: () => {
          this.logsLoading.set(false);
          this.toast.error('Could not load the delivery log');
        },
      });
  }

  onLogsScopeChange(scope: LogScope) {
    this.logsScope.set(scope);
    this.logsPage.set(1);
    this.expandedLogs.set(new Set());
    this.loadLogs();
  }

  /** p-paginator page event: { first, rows, page (0-based), pageCount } */
  onLogsPage(e: any) {
    this.logsPage.set((e?.page ?? 0) + 1);
    this.logsPageSize.set(e?.rows ?? this.logsPageSize());
    this.expandedLogs.set(new Set());
    this.loadLogs();
  }

  closeLogs() {
    this.showLogs.set(false);
  }

  logsTitle(): string {
    const cb = this.logsFor();
    if (cb) return `Delivery Log — ${cb.eventName} → ${this.trunc(cb.callbackUrl, 42)}`;
    const id = this.clientId() ?? this.filterClientId();
    return id != null ? 'Delivery Log — all callbacks of this client' : 'Delivery Log — all clients';
  }

  isLogExpanded(id: number): boolean {
    return this.expandedLogs().has(id);
  }

  toggleLogExpand(id: number) {
    this.expandedLogs.update((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  /** Pretty-print a JSON payload for the expanded row; fall back to the raw text. */
  pretty(v?: string | null): string {
    const s = (v ?? '').toString().trim();
    if (!s) return '—';
    try {
      return JSON.stringify(JSON.parse(s), null, 2);
    } catch {
      return s;
    }
  }

  // ===================== utils =====================
  private patchRow(id: number, patch: Partial<ClientCallback>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  private nz(v: any): string | null {
    const s = (v ?? '').toString().trim();
    return s ? s : null;
  }
}
